// biome-ignore lint/style/useImportType: <explanation>
import type { ConnectorLabelOffsetAnchor } from '@blocksuite/affine-model';
import type { PointStyle } from '@blocksuite/affine-model';
import {
  DEFAULT_FRONT_END_POINT_STYLE,
  DEFAULT_REAR_END_POINT_STYLE,
} from '@blocksuite/affine-model';
import {
  type Color,
  ConnectorMode,
  FontStyle,
  FontWeight,
  StrokeStyle,
  TextAlign,
} from '@blocksuite/affine-model';
import type { SerializedXYWH } from '@blocksuite/global/utils';
import type { Y } from '@blocksuite/store';
import { DocCollection } from '@blocksuite/store';

export enum ShapeType {
  Rect = 'rect',
  Diamond = 'diamond',
  Triangle = 'triangle',
  Ellipse = 'ellipse',
}

export enum ShapeStyle {
  General = 'general',
  HandDrawn = 'hand-drawn',
}

// Base styles that are shared between nodes and theme
export type BaseShapeStyle = {
  radius: number;
  strokeWidth: number;
  strokeColor: string;
  color: Color;
  filled: boolean;
  fillColor: Color;
  padding: [number, number];
  shadow?: {
    blur: number;
    offsetX: number;
    offsetY: number;
    color: string;
  };
  shapeType?: ShapeType;
  shapeStyle?: ShapeStyle;
  strokeStyle?: StrokeStyle;
};

export type BaseLabelStyle = {
  labelDisplay?: boolean;
  labelEditing?: boolean;
  labelOffset?: {
    distance: number;
    anchor: ConnectorLabelOffsetAnchor;
  };
  labelStyle?: {
    color: Color;
    fontFamily: string;
    fontSize: number;
    fontStyle: FontStyle;
    fontWeight: FontWeight;
    textAlign: TextAlign;
  };
  labelConstraints?: {
    hasMaxWidth: boolean;
    maxWidth: number;
  };
  labelXYWH?: [number, number, number, number];
};

export type BaseConnectorStyle = {
  strokeStyle: StrokeStyle;
  stroke: Color;
  strokeWidth: number;
  mode: ConnectorMode;
  frontEndpointStyle: PointStyle;
  rearEndpointStyle: PointStyle;
  rough: boolean;
  roughness: number;
} & BaseLabelStyle;


export type MermaidShapeNode = {
  id: string;
  text: string;
  type: 'shape';
  shapeType: ShapeType;
  xywh?: SerializedXYWH;
  children?: MermaidNode[];
  meta?: {
    index?: number;
    parent?: string;
    style?: BaseShapeStyle;
  };
};

export type MermaidConnectorNode = {
  id: string;
  text: string;
  type: 'connector';
  xywh?: SerializedXYWH;
  children?: MermaidShapeNode[];
  meta?: {
    index?: number;
    parent?: string;
    style?: BaseConnectorStyle;
    path?: string;
  };
};

export type MermaidNode = MermaidShapeNode | MermaidConnectorNode;

// Type guards remain the same
export function isShapeNode(node: MermaidNode): node is MermaidShapeNode {
  return node.type === 'shape';
}

export function isConnectorNode(
  node: MermaidNode
): node is MermaidConnectorNode {
  return node.type === 'connector';
}

// Theme types derived from node types
export type ThemeShapeStyle = {
  fontSize: number;
  fontFamily: string;
  fontWeight: FontWeight;
  radius: number;
  strokeWidth: number;
  strokeColor: string;
  color: string;
  filled: boolean;
  fillColor: string;
  shadow: {
    blur: number;
    offsetX: number;
    offsetY: number;
    color: string;
  };
  shapeStyle: ShapeStyle;
  strokeStyle: StrokeStyle;
  padding: [number, number];
};

type ThemeConnectorStyle = BaseConnectorStyle;

export type MermaidTheme = {
  background?: string;
  connectorColor?: Color;
  textColor?: Color;
  spacing?: {
    horizontal: number;
    vertical: number;
  };
  shape?: ThemeShapeStyle;
  connector?: ThemeConnectorStyle;
};

export type MermaidTemplate = {
  blocks: MermaidNode[];
  meta: {
    type: string;
    direction?: 'TB' | 'BT' | 'LR' | 'RL';
    theme?: MermaidTheme;
  };
  originalSvg?: string;
  vectorIllustration?: string | null;
  vectorNodeText?: string | null;
};

// Default theme using the new types
export const defaultTheme: Required<MermaidTheme> = {
  background: '#ffffff',
  connectorColor: '#000000',
  textColor: '#000000',
  spacing: {
    horizontal: 50,
    vertical: 50,
  },
  shape: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: FontWeight.Regular,
    radius: 4,
    strokeWidth: 2,
    strokeColor: '--affine-secondary-color',
    color: '--affine-text-primary-color',
    filled: true,
    fillColor: '--affine-background-primary-color',
    shadow: {
      blur: 0,
      offsetX: 0,
      offsetY: 0,
      color: '--affine-shadow-color',
    },
    shapeStyle: ShapeStyle.General,
    strokeStyle: StrokeStyle.Solid,
    padding: [10, 10],
  },
  connector: {
    stroke: '#c0bfbf57',
    strokeWidth: 2,
    mode: ConnectorMode.Curve,
    strokeStyle: StrokeStyle.Dash,
    frontEndpointStyle: DEFAULT_FRONT_END_POINT_STYLE,
    rearEndpointStyle: DEFAULT_REAR_END_POINT_STYLE,
    rough: false,
    roughness: 1.4,
    labelStyle: {
      color: '--affine-text-primary-color',
      fontFamily: 'Inter',
      fontSize: 12,
      fontStyle: FontStyle.Normal,
      fontWeight: FontWeight.Regular,
      textAlign: TextAlign.Center,
    },
  },
} as const;

export function createYText(text: string): Y.Text {
  const yText = new DocCollection.Y.Text();
  yText.insert(0, text);
  return yText;
}

export function processConnectorId(
  id: string
): { source: string; target: string } | null {
  const match = id.match(/([A-Za-z0-9]+)\s*-+>\s*([A-Za-z0-9]+)/);
  if (!match) return null;
  const source = match[1];
  const target = match[2];
  if (!source || !target) return null;
  return { source, target };
}

export function createShapeNode(
  id: string,
  text: string,
  index: number,
  theme: Required<MermaidTheme>,
  parent?: string,
  xywh?: SerializedXYWH,
  shapeType?: ShapeType
): MermaidShapeNode {
  const connectorInfo = processConnectorId(id);
  const actualId = connectorInfo ? connectorInfo.target : id;
  const finalShapeType = shapeType ?? ShapeType.Rect;

  return {
    id: actualId,
    text: text,
    type: 'shape',
    shapeType: finalShapeType,
    xywh,
    meta: {
      index,
      parent,
      style: {
        fillColor: theme.shape.fillColor,
        strokeColor: theme.shape.strokeColor,
        color: theme.shape.color,
        filled: theme.shape.filled,
        strokeWidth: theme.shape.strokeWidth,
        radius: theme.shape.radius,
        shapeType: finalShapeType,
        shapeStyle: theme.shape.shapeStyle,
        strokeStyle: theme.shape.strokeStyle,
        padding: theme.shape.padding,
      },
    },
  };
}

export function createConnectorNode(
  source: string,
  target: string,
  theme: Required<MermaidTheme>,
  index: number,
  text: string,
  _sourceEdgesCount: number,
  labelXYWH?: [number, number, number, number]
): MermaidConnectorNode {
  const connectorHasText = text.trim() !== '';
  const mode = ConnectorMode.Curve;

  // If the text has line breaks, adjust the label height
  let finalLabelXYWH: [number, number, number, number] | undefined = labelXYWH;
  if (!finalLabelXYWH && connectorHasText) {
    const lineCount = text.split('\n').length;
    const width = theme.connector.labelXYWH?.[2] || 120;
    const height = Math.max(
      theme.connector.labelXYWH?.[3] || 20,
      lineCount * 18
    );
    finalLabelXYWH = [0, 0, width, height];
  }

  return {
    id: `connector_${source}_${target}`,
    text,
    type: 'connector',
    meta: {
      index,
      parent: source,
      style: {
        stroke: theme.connector.stroke,
        strokeWidth: theme.connector.strokeWidth,
        mode,
        strokeStyle: theme.connector.strokeStyle,
        frontEndpointStyle: theme.connector.frontEndpointStyle,
        rearEndpointStyle: theme.connector.rearEndpointStyle,
        rough: theme.connector.rough,
        roughness: theme.connector.roughness,
        labelXYWH: finalLabelXYWH || theme.connector.labelXYWH,
        labelDisplay: connectorHasText,
        labelStyle: theme.connector.labelStyle,
      },
    },
  };
}

export function applyThemeToNodes(
  nodes: MermaidNode[],
  theme: Required<MermaidTheme>
): MermaidNode[] {
  let index = 0;
  const processedNodes: MermaidNode[] = [];
  const processedIds = new Set<string>();

  // First pass: Process all shape nodes
  for (const node of nodes) {
    if (isShapeNode(node)) {
      const shapeNode = createShapeNode(
        node.id,
        node.text.toString(),
        index++,
        theme,
        node.meta?.parent,
        node.xywh,
        node.shapeType
      );
      if (!processedIds.has(shapeNode.id)) {
        processedNodes.push(shapeNode);
        processedIds.add(shapeNode.id);
      }
    }
  }

  // Second pass: Process all connector nodes
  for (const node of nodes) {
    if (isConnectorNode(node)) {
      const parts = node.id.replace(/^connector_/, '').split('_');
      const sourceId = node.meta?.parent || '';
      const targetId = parts[1] || '';

      const sourceEdgesCount = nodes.filter(
        n => isConnectorNode(n) && n.meta?.parent === sourceId
      ).length;

      const connectorNode = createConnectorNode(
        sourceId,
        `flowchart-${targetId}`,
        theme,
        index++,
        node?.text ?? '',
        sourceEdgesCount
      );
      processedNodes.push(connectorNode);
    }
  }

  return processedNodes;
}

function createBasicFlowchart(nodes: MermaidNode[]): MermaidTemplate {
  const theme = defaultTheme as Required<MermaidTheme>;
  const direction = 'TB' as const;
  return {
    blocks: applyThemeToNodes(nodes, theme),
    meta: {
      type: 'flowchart',
      direction,
      theme,
    },
  };
}

function createBasicSequence(nodes: MermaidNode[]): MermaidTemplate {
  const theme = defaultTheme as Required<MermaidTheme>;
  return {
    blocks: applyThemeToNodes(nodes, theme),
    meta: {
      type: 'sequence',
      theme,
    },
  };
}

function createBasicClass(nodes: MermaidNode[]): MermaidTemplate {
  const theme = defaultTheme as Required<MermaidTheme>;
  return {
    blocks: applyThemeToNodes(nodes, theme),
    meta: {
      type: 'class',
      theme,
    },
  };
}

export function createMermaidTemplate(
  type: string,
  nodes: MermaidNode[]
): MermaidTemplate {
  switch (type.toLowerCase()) {
    case 'flowchart':
      return createBasicFlowchart(nodes);
    case 'sequence':
      return createBasicSequence(nodes);
    case 'class':
      return createBasicClass(nodes);
    default:
      return createBasicFlowchart(nodes);
  }
}
